<template>
  <div class="user-management-container">
    <div class="user-management-header">
      <h1>Gestión de Usuarios</h1>
      <Button 
        label="Nuevo Usuario" 
        icon="pi pi-plus" 
        @click="openNewUserDialog" 
        class="p-button-success"
      />
    </div>

    <Card>
      <template #content>
        <DataTable 
          :value="users" 
          :loading="loading"
          class="users-table"
          :paginator="users.length > 10"
          :rows="10"
          stripedRows
          responsiveLayout="scroll"
        >
          <Column field="email" header="Correo Electrónico"></Column>
          <Column field="full_name" header="Nombre Completo"></Column>
          <Column field="country" header="País"></Column>
          <Column field="is_admin" header="Administrador">
            <template #body="slotProps">
              <span :class="slotProps.data.is_admin ? 'user-admin' : 'user-tech'">
                {{ slotProps.data.is_admin ? 'Administrador' : 'Técnico' }}
              </span>
            </template>
          </Column>
          <Column field="is_active" header="Estado">
            <template #body="slotProps">
              <span :class="slotProps.data.is_active ? 'user-active' : 'user-inactive'">
                {{ slotProps.data.is_active ? 'Activo' : 'Inactivo' }}
              </span>
            </template>
          </Column>
          <Column header="Acciones">
            <template #body="slotProps">
              <div class="action-buttons">
                <Button 
                  icon="pi pi-pencil" 
                  class="p-button-rounded p-button-text p-button-info" 
                  @click="editUser(slotProps.data)" 
                />
                <Button 
                  icon="pi pi-trash" 
                  class="p-button-rounded p-button-text p-button-danger" 
                  @click="confirmDeleteUser(slotProps.data)" 
                />
              </div>
            </template>
          </Column>
        </DataTable>
      </template>
    </Card>

    <!-- Diálogo para crear/editar usuario -->
    <Dialog 
      v-model:visible="userDialog" 
      :style="{width: '450px'}" 
      :header="editMode ? 'Editar Usuario' : 'Nuevo Usuario'" 
      :modal="true"
      class="p-fluid"
    >
      <div class="form-group">
        <label for="email">Correo Electrónico</label>
        <InputText id="email" v-model="user.email" :disabled="editMode" required autofocus />
      </div>
      
      <div class="form-group">
        <label for="full_name">Nombre Completo</label>
        <InputText id="full_name" v-model="user.full_name" required />
      </div>
      
      <div class="form-group">
        <label for="password">Contraseña</label>
        <Password id="password" v-model="user.password" :feedback="false" toggleMask />
        <small v-if="editMode" class="info-text">Dejar en blanco para mantener la contraseña actual</small>
      </div>
      
      <div class="form-group">
        <label for="country">País</label>
        <Dropdown id="country" v-model="user.country" :options="countries" optionLabel="name" optionValue="code" placeholder="Selecciona un país" required />
      </div>
      
      <div class="form-group checkbox-group">
        <label for="is_admin">Administrador</label>
        <div class="p-field-checkbox">
          <input type="checkbox" id="is_admin" v-model="user.is_admin" />
        </div>
      </div>
      
      <div class="form-group checkbox-group">
        <label for="is_active">Activo</label>
        <div class="p-field-checkbox">
          <input type="checkbox" id="is_active" v-model="user.is_active" />
        </div>
      </div>
      
      <template #footer>
        <Button label="Cancelar" icon="pi pi-times" class="p-button-text" @click="closeDialog" />
        <Button label="Guardar" icon="pi pi-check" class="p-button-text" @click="saveUser" :loading="saving" />
      </template>
    </Dialog>

    <!-- Diálogo de confirmación para eliminar usuario -->
    <Dialog 
      v-model:visible="deleteUserDialog" 
      :style="{width: '450px'}" 
      header="Confirmar Eliminación" 
      :modal="true"
    >
      <div class="confirmation-content">
        <i class="pi pi-exclamation-triangle mr-3" style="font-size: 2rem" />
        <span v-if="user">¿Estás seguro de que deseas eliminar a <b>{{ user.email }}</b>?</span>
      </div>
      <template #footer>
        <Button label="No" icon="pi pi-times" class="p-button-text" @click="deleteUserDialog = false" />
        <Button label="Sí" icon="pi pi-check" class="p-button-text p-button-danger" @click="deleteUser" :loading="deleting" />
      </template>
    </Dialog>

    <!-- Mensaje de notificación -->
    <div v-if="success" class="success-message">
      {{ success }}
    </div>
    <div v-if="error" class="error-message">
      {{ error }}
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'

export default {
  name: 'UserManagementView',
  data() {
    return {
      user: {
        email: '',
        full_name: '',
        password: '',
        country: '',
        is_admin: false,
        is_active: true
      },
      userDialog: false,
      deleteUserDialog: false,
      editMode: false,
      saving: false,
      deleting: false,
      countries: [
        { name: 'Argentina', code: 'Argentina' },
        { name: 'Brasil', code: 'Brasil' },
        { name: 'México', code: 'México' }
      ]
    }
  },
  computed: {
    ...mapState('users', ['users', 'loading', 'error', 'success'])
  },
  methods: {
    ...mapActions('users', ['fetchUsers', 'createUser', 'updateUser', 'deleteUser', 'clearErrors']),
    
    openNewUserDialog() {
      this.user = {
        email: '',
        full_name: '',
        password: '',
        country: '',
        is_admin: false,
        is_active: true
      }
      this.editMode = false
      this.userDialog = true
    },
    
    editUser(user) {
      this.user = { ...user, password: '' } // No incluimos la contraseña en la edición
      this.editMode = true
      this.userDialog = true
    },
    
    closeDialog() {
      this.userDialog = false
      this.clearErrors()
    },
    
    async saveUser() {
      this.saving = true
      
      try {
        // Validación básica
        if (!this.user.email || !this.user.country) {
          alert('Por favor completa los campos requeridos')
          this.saving = false
          return
        }
        
        // Si es nuevo usuario, la contraseña es obligatoria
        if (!this.editMode && !this.user.password) {
          alert('La contraseña es requerida para nuevos usuarios')
          this.saving = false
          return
        }
        
        let result
        
        if (this.editMode) {
          // Eliminar campos vacíos para no enviarlos
          const userData = { ...this.user }
          if (!userData.password) delete userData.password
          
          result = await this.updateUser({
            userId: this.user.id,
            userData
          })
        } else {
          result = await this.createUser(this.user)
        }
        
        if (result) {
          this.userDialog = false
        }
      } finally {
        this.saving = false
      }
    },
    
    confirmDeleteUser(user) {
      this.user = user
      this.deleteUserDialog = true
    },
    
    async deleteUser() {
      this.deleting = true
      
      try {
        const success = await this.deleteUser(this.user.id)
        if (success) {
          this.deleteUserDialog = false
        }
      } finally {
        this.deleting = false
      }
    }
  },
  created() {
    // Cargar los usuarios al iniciar la vista
    this.fetchUsers()
    
    // Limpiar mensajes
    this.clearErrors()
  }
}
</script>

<style scoped>
.user-management-container {
  max-width: 1200px;
  margin: 20px auto;
  padding: 0 20px;
}

.user-management-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.users-table {
  width: 100%;
}

.action-buttons {
  display: flex;
  gap: 5px;
  justify-content: center;
}

.form-group {
  margin-bottom: 15px;
}

.form-group label {
  display: block;
  margin-bottom: 5px;
  font-weight: 600;
}

.checkbox-group {
  display: flex;
  align-items: center;
  gap: 10px;
}

.user-admin {
  color: #2c3e50;
  font-weight: bold;
}

.user-tech {
  color: #7f8c8d;
}

.user-active {
  color: #27ae60;
}

.user-inactive {
  color: #e74c3c;
}

.info-text {
  color: #7f8c8d;
  font-style: italic;
}

.confirmation-content {
  display: flex;
  align-items: center;
  gap: 10px;
}

.success-message {
  position: fixed;
  bottom: 20px;
  right: 20px;
  padding: 15px 25px;
  background-color: #dff0d8;
  color: #2ecc71;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  z-index: 999;
}

.error-message {
  position: fixed;
  bottom: 20px;
  right: 20px;
  padding: 15px 25px;
  background-color: #f8d7da;
  color: #e74c3c;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  z-index: 999;
}
</style> 